'use client';
import ExpiredLink from '@/components/expired-link';
import FormattedMessage from '@/components/formatted-message';
import Logo from '@/components/logo';
import { Button } from '@/components/ui/button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { PasswordInput } from '@/components/ui/input-password';
import { toast } from '@/hooks/use-toast';
import { resetPasswordMutationFn } from '@/lib/api';
import { useZodForm } from '@/lib/zod';
import { ResetPasswordSchema } from '@/schemas';
import { TResetPassword } from '@/types/types';
import { useMutation } from '@tanstack/react-query';
import { Loader } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function ResetPassword() {
    const navigate = useNavigate();

    const [params] = useSearchParams();
    const token = params.get('token');
    const exp = Number(params.get('exp'));
    const now = Date.now();

    const isExpired = token && exp && exp <= now;

    const { mutate, isPending } = useMutation({
        mutationFn: resetPasswordMutationFn,
    });

    const form = useZodForm(ResetPasswordSchema, {
        defaultValues: {
            password: '',
            confirmPassword: '',
        },
    });

    const onSubmit = (values: TResetPassword) => {
        if (!token) {
            navigate('/forgot-password');
            return;
        }
        const data = {
            password: values.password,
            resetPasswordToken: token,
        };
        mutate(data, {
            onSuccess: ({ data }) => {
                toast({
                    title: 'Success',
                    description: <FormattedMessage message={data.message} />,
                });
                navigate('/login');
            },
            onError: (error) => {
                toast({
                    title: 'Error',
                    description: <FormattedMessage message={error.message} />,
                    variant: 'destructive',
                    className: 'text-sm',
                });
            },
        });
    };

    useEffect(() => {
        const sub = form.watch((_, { name }) => {
            if (name === 'password' || name === 'confirmPassword') {
                form.trigger(['password', 'confirmPassword']);
            }
        });
        return () => {
            sub.unsubscribe();
        };
    }, [form]);

    return (
        <main className='w-full min-h-[590px] h-full max-w-full flex items-center justify-center '>
            <>
                {isExpired ? (
                    <ExpiredLink
                        title='Invalid or expired reset link'
                        message='You can request a new password reset link'
                        url='/forgot-password'
                        btnTitle='Go to forgot password'
                    />
                ) : (
                    <div className='w-full h-full p-5 rounded-md'>
                        <Logo size='100px' />
                        <h1
                            className='text-xl tracking-[-0.16px] dark:text-[#fcfdffef] font-bold mb-1.5 mt-8
        text-center'>
                            Set up a new password
                        </h1>
                        <p className='mb-6 text-center text-[15px] dark:text-[#f1f7feb5] font-normal'>
                            Your password must be different from your previous
                            one.
                        </p>
                        <Form {...form}>
                            <form
                                className='flex flex-col gap-6'
                                onSubmit={form.handleSubmit(onSubmit)}>
                                <div className='mb-0'>
                                    <FormField
                                        control={form.control}
                                        name='password'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className='dark:text-[#f1f7feb5] text-sm'>
                                                    New password
                                                </FormLabel>
                                                <FormControl>
                                                    <PasswordInput
                                                        autoComplete='off'
                                                        placeholder='Enter your password'
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                <div className='mb-0'>
                                    <FormField
                                        control={form.control}
                                        name='confirmPassword'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className='dark:text-[#f1f7feb5] text-sm'>
                                                    Confirm new password
                                                </FormLabel>
                                                <FormControl>
                                                    <PasswordInput
                                                        autoComplete='off'
                                                        placeholder='Enter your password again'
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <Button
                                    disabled={isPending}
                                    className='w-full text-[15px] h-[40px] text-white font-semibold'>
                                    {isPending && (
                                        <Loader className='animate-spin' />
                                    )}
                                    Reset password
                                </Button>
                            </form>
                        </Form>
                    </div>
                )}
            </>
        </main>
    );
}
